import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

export const DEFAULT_COUNTRY_ISO = 'BR';

const regionNames = new Intl.DisplayNames(['pt-BR'], { type: 'region' });
const maxDigitsByCountry = new Map();

export function normalizePhoneSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function countryFlagEmoji(iso) {
  return String(iso || '')
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

function buildCountryOption(iso) {
  const ddi = getCountryCallingCode(iso);
  const name = regionNames.of(iso) || iso;
  const example = getExampleNumber(iso, examples);
  const placeholder = example?.formatNational() || 'Número de telefone';

  return {
    iso,
    ddi,
    name,
    flag: countryFlagEmoji(iso),
    placeholder,
    searchText: normalizePhoneSearch(`${iso} ${name} ${ddi}`),
  };
}

export const PHONE_COUNTRIES = getCountries()
  .map(buildCountryOption)
  .sort((a, b) => {
    if (a.iso === DEFAULT_COUNTRY_ISO) return -1;
    if (b.iso === DEFAULT_COUNTRY_ISO) return 1;
    return a.name.localeCompare(b.name, 'pt-BR');
  });

export function getDefaultPhoneCountry() {
  return PHONE_COUNTRIES.find((country) => country.iso === DEFAULT_COUNTRY_ISO) || PHONE_COUNTRIES[0];
}

export function formatPhoneForCountry(value, country) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return new AsYouType(country?.iso || DEFAULT_COUNTRY_ISO).input(digits);
}

export function getMaxPhoneDigitsForCountry(country) {
  const iso = country?.iso || DEFAULT_COUNTRY_ISO;
  if (maxDigitsByCountry.has(iso)) return maxDigitsByCountry.get(iso);

  let maxDigits = 15;
  for (let length = 1; length <= 20; length += 1) {
    const validation = validatePhoneNumberLength('9'.repeat(length), iso);
    if (validation === 'TOO_LONG') {
      maxDigits = length - 1;
      break;
    }
  }

  maxDigitsByCountry.set(iso, maxDigits);
  return maxDigits;
}

export function trimPhoneDigitsForCountry(value, country) {
  const maxDigits = getMaxPhoneDigitsForCountry(country);
  return String(value || '').replace(/\D/g, '').slice(0, maxDigits);
}

export function formatLimitedPhoneForCountry(value, country) {
  const digits = trimPhoneDigitsForCountry(value, country);
  if (!digits) return '';
  return formatPhoneForCountry(digits, country);
}

export function getPhoneInputMaxLength(country) {
  const maxDigits = getMaxPhoneDigitsForCountry(country);
  return formatPhoneForCountry('9'.repeat(maxDigits), country).length;
}

export function isPossiblePhoneForCountry(value, country) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return false;
  return !validatePhoneNumberLength(digits, country?.iso || DEFAULT_COUNTRY_ISO);
}
